// src/components/WelcomeDialog.tsx
import React, { useState, useEffect } from 'react';
import welcome1 from '../assets/images/welcome-1.webp';
import welcome2 from '../assets/images/welcome-2.webp';

interface WelcomeDialogProps {
    onComplete: () => void;
}

export const WelcomeDialog: React.FC<WelcomeDialogProps> = ({ onComplete }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    const images = [welcome1, welcome2];

    const titles = [
        'Добро пожаловать в Ruyo Market!',
        'Установите приложение'
    ];

    const descriptions = [
        'Лучшие товары с быстрой доставкой по всему Таджикистану',
        'Быстрый доступ, работа офлайн и удобные уведомления'
    ];

    // Номер телефона
    const phoneNumber = '+992991003004';
    const formattedPhone = '+992 991-003-004';

    // Функции для связи
    const openWhatsApp = () => {
        const url = `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}`;
        window.open(url, '_blank');
    };

    const openTelegram = () => {
        const url = `https://t.me/+${phoneNumber.replace(/[^0-9]/g, '')}`;
        window.open(url, '_blank');
    };

    useEffect(() => {
        // Предзагрузка изображений
        const preloadImages = async () => {
            const promises = images.map((src) => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.src = src;
                    img.onload = resolve;
                    img.onerror = reject;
                });
            });

            try {
                await Promise.all(promises);
                setIsLoading(false);
                setIsVisible(true);
            } catch (error) {
                console.error('Error loading images:', error);
                setIsLoading(false);
                setIsVisible(true);
            }
        };

        preloadImages();
    }, []);

    const handleNext = () => {
        if (currentStep < images.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            handleComplete();
        }
    };

    const handleComplete = () => {
        setIsVisible(false);
        onComplete();
    };

    const handleSkip = () => {
        setIsVisible(false);
        onComplete();
    };

    if (isLoading) {
        return (
            <div className="welcome-overlay">
                <div className="welcome-content">
                    <div className="welcome-loader">
                        <div className="spinner"></div>
                        <p>Загрузка...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!isVisible) {
        return null;
    }

    return (
        <div className="welcome-overlay" onClick={handleSkip}>
            <div className="welcome-content" onClick={(e) => e.stopPropagation()}>
                <button className="welcome-skip" onClick={handleSkip}>
                    Пропустить
                </button>

                <div className="welcome-image-container">
                    <img
                        src={images[currentStep]}
                        alt={`Welcome ${currentStep + 1}`}
                        className="welcome-image"
                    />
                </div>

                {/* Показываем контактные данные ТОЛЬКО на первом шаге */}
                {currentStep === 0 && (
                    <div className="welcome-contact">
                        <div className="contact-phone">
                            📞 {formattedPhone}
                        </div>
                        <div className="contact-buttons">
                            <button
                                className="contact-btn whatsapp"
                                onClick={openWhatsApp}
                            >
                                💬 WhatsApp
                            </button>
                            <button
                                className="contact-btn telegram"
                                onClick={openTelegram}
                            >
                                ✈️ Telegram
                            </button>
                        </div>
                        <div className="contact-note">
                            Свяжитесь с нами для получения помощи
                        </div>
                    </div>
                )}

                <div className="welcome-dots">
                    {images.map((_, index) => (
                        <div
                            key={index}
                            className={`welcome-dot ${currentStep === index ? 'active' : ''}`}
                            onClick={() => setCurrentStep(index)}
                        />
                    ))}
                </div>

                <button className="welcome-button" onClick={handleNext}>
                    {currentStep === images.length - 1 ? 'Завершить' : 'Продолжить'}
                </button>
            </div>
        </div>
    );
};

export default WelcomeDialog;